export const formatTransactionDate = (value?: string | number | Date) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getResponseRoot = (response: any) =>
  response?.data?.result ??
  response?.data?.data ??
  response?.data ??
  response ??
  null;

export const extractTransactionListFromResponse = (response: any): any[] => {
  const root = getResponseRoot(response);
  if (Array.isArray(root)) return root;

  const list =
    root?.transactions || root?.list || root?.items || root?.rows || [];

  return Array.isArray(list) ? list : [];
};

export const resolveTransactionsHasMore = (
  response: any,
  receivedCount: number,
  limit: number,
  offset: number,
) => {
  const root = getResponseRoot(response);
  const pagination = root?.pagination || root?.pageInfo || root?.meta || {};

  const hasMoreFlag = root?.hasMore ?? pagination?.hasMore ?? pagination?.has_next;
  if (typeof hasMoreFlag === "boolean") return hasMoreFlag;

  const totalPages = Number(
    pagination?.totalPages ?? pagination?.pages ?? root?.totalPages,
  );
  const currentPage = Number(
    pagination?.currentPage ?? pagination?.page ?? root?.page ?? offset,
  );
  if (
    Number.isFinite(totalPages) &&
    totalPages > 0 &&
    Number.isFinite(currentPage)
  ) {
    return currentPage < totalPages;
  }

  const total = Number(
    pagination?.total ??
      pagination?.totalCount ??
      root?.total ??
      root?.count ??
      root?.totalCount,
  );
  if (Number.isFinite(total) && total >= 0) {
    return offset * limit < total;
  }

  return receivedCount >= limit;
};

export const normalizeTransactionNumber = (value: any) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

export const buildTransactionMeta = (transaction: any) => {
  const eventType = String(
    transaction?.eventType || transaction?.event || "",
  )
    .trim()
    .toUpperCase();
  const eventTypeTitleMap: Record<string, string> = {
    FOLLOW: "Follow as a Viewer",
    LIKE: "Like as a Viewer",
    COMMENT: "Comment as a Viewer",
    CREATE: "Create Content",
    CREATE_CONTENT: "Create Content",
    CONTENT_CREATION: "Create Content",
    DAILY_CHECKIN: "Daily Check-in",
    DAILY_CHECK_IN: "Daily Check-in",
    UNBLOCK_EPISODE: "Unlocked Episode",
    UNLOCK_EPISODE: "Unlocked Episode",
  };
  const eventTypeSubtitleMap: Record<string, string> = {
    DAILY_CHECKIN: "Claimed Daily Check-in Reward",
    DAILY_CHECK_IN: "Claimed Daily Check-in Reward",
  };
  const title =
    eventTypeTitleMap[eventType] ||
    transaction?.title ||
    transaction?.name ||
    transaction?.event ||
    transaction?.transactionType ||
    "Transaction";
  const subtitle =
    eventTypeSubtitleMap[eventType] ||
    transaction?.description ||
    transaction?.details ||
    transaction?.remarks ||
    transaction?.note ||
    "";
  const date =
    transaction?.createdTimestamp ||
    transaction?.createdAt ||
    transaction?.created_at ||
    transaction?.date ||
    transaction?.transactionDate ||
    "";
  const flow = String(
    transaction?.transactionFlow ||
      transaction?.flow ||
      transaction?.type ||
      "",
  ).toLowerCase();
  const rawAmount = normalizeTransactionNumber(
    transaction?.transactionAmount ??
      transaction?.coins ??
      transaction?.amount ??
      transaction?.value,
  );
  const isCredit =
    flow === "credit" ||
    flow === "add" ||
    flow === "earn" ||
    flow === "reward" ||
    rawAmount > 0;
  const amount = rawAmount !== 0 ? Math.abs(rawAmount) : 0;
  return {
    title,
    subtitle,
    date: formatTransactionDate(date),
    amount,
    isCredit,
  };
};

export const buildTransactionId = (transaction: any, meta: any) =>
  transaction?.id ||
  transaction?._id ||
  transaction?.transactionId ||
  `${meta.title}-${meta.date}`;

export const buildTransactionIconLabel = (title: string) =>
  title
    .split(" ")
    .slice(0, 2)
    .map((item: string) => item[0])
    .join("")
    .toUpperCase();
